import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Mail, CheckCircle2, ClipboardList, AlertTriangle, FileText, Activity } from "lucide-react";
import { useRole } from "../context/RoleContext";
import { listNotifications, markAllRead, markRead, onNotificationsUpdated, relativeTime, unreadCount } from "../lib/notifications";

const POLL_MS = 30000;

const CATEGORY_ICON = {
  messages: Mail,
  approvals: CheckCircle2,
  status: Activity,
  documents: FileText,
  warnings: AlertTriangle,
};

const CATEGORY_COLOR = {
  messages: "#1B1F4B",
  approvals: "#4F6149",
  status: "#3A5563",
  documents: "#B98A3C",
  warnings: "#A23B2E",
};

function CategoryIcon({ category }) {
  const Icon = CATEGORY_ICON[category] || ClipboardList;
  return <Icon size={15} strokeWidth={1.6} color={CATEGORY_COLOR[category] || "#272636"} />;
}

export default function NotificationBell() {
  const { user } = useRole();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);

  const refreshCount = useCallback(async () => {
    if (!user?.id) return;
    try {
      const u = await unreadCount(user.id);
      setUnread(u);
    } catch {
      // silent — bell is a background poller, do not surface
    }
  }, [user?.id]);

  const loadPanel = useCallback(async () => {
    if (!user?.id) return;
    try {
      const data = await listNotifications(user.id, { limit: 5 });
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {
      setItems([]);
    }
  }, [user?.id]);

  // Initial fetch + 30s polling
  useEffect(() => {
    if (!user?.id) return;
    refreshCount();
    const id = setInterval(refreshCount, POLL_MS);
    return () => clearInterval(id);
  }, [user?.id, refreshCount]);

  // Cross-component refresh: any mark-read elsewhere triggers an immediate
  // refresh so the badge stays in sync without waiting for the 30s poll.
  useEffect(() => {
    if (!user?.id) return;
    const off = onNotificationsUpdated(() => {
      refreshCount();
      // If the panel is open, also refresh its contents.
      setOpen((isOpen) => {
        if (isOpen) loadPanel();
        return isOpen;
      });
    });
    return off;
  }, [user?.id, refreshCount, loadPanel]);

  // Load panel content when opened
  useEffect(() => {
    if (open) loadPanel();
  }, [open, loadPanel]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!user?.id) return null;

  const onItemClick = async (n) => {
    setOpen(false);
    if (!n.read) {
      try {
        await markRead(n.id, user.id);
      } catch {
        // ignore mark-read failure; we still navigate
      }
    }
    if (n.route) navigate(n.route);
    else navigate("/notifications");
  };

  const onMarkAll = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await markAllRead(user.id);
      setUnread(0);
      setItems((items || []).map((n) => ({ ...n, read: true })));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }} data-testid="notification-bell-wrap">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}`}
        data-testid="notification-bell-btn"
        style={{
          position: "relative",
          width: 38,
          height: 38,
          borderRadius: 10,
          border: "1px solid rgba(251,250,253,0.18)",
          background: open ? "rgba(251,250,253,0.10)" : "transparent",
          color: "#FBFAFD",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "background-color 160ms ease",
        }}
      >
        <Bell size={17} strokeWidth={1.6} />
        {unread > 0 && (
          <span
            data-testid="notification-bell-badge"
            className="tabular-nums"
            style={{
              position: "absolute",
              top: -4,
              right: -4,
              minWidth: 18,
              height: 18,
              padding: "0 5px",
              borderRadius: 999,
              background: "#B14E2F",
              color: "#FBFAFD",
              fontSize: 10,
              fontWeight: 800,
              lineHeight: "18px",
              textAlign: "center",
              border: "1.5px solid #1B1F4B",
            }}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          style={{
            position: "absolute",
            top: 46,
            right: 0,
            width: 380,
            maxWidth: "calc(100vw - 32px)",
            background: "#FBFAFD",
            border: "1px solid #D9D8E4",
            borderRadius: 8,
            boxShadow: "0 12px 32px rgba(27,31,75,0.18)",
            overflow: "hidden",
            zIndex: 60,
          }}
        >
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid #D9D8E4",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div
                style={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  fontWeight: 600,
                  fontSize: 16,
                  color: "#1B1F4B",
                }}
              >
                Notifications.
              </div>
              <div className="wn-label" style={{ marginTop: 2 }}>
                <span className="tabular-nums">{unread}</span> unread
              </div>
            </div>
            {unread > 0 && (
              <button
                type="button"
                className="wn-btn wn-btn-tertiary"
                onClick={onMarkAll}
                disabled={busy}
                style={{ fontSize: 12 }}
                data-testid="notification-mark-all-btn"
              >
                Mark all read
              </button>
            )}
          </div>

          <div style={{ maxHeight: 380, overflowY: "auto" }}>
            {items === null && (
              <div style={{ padding: 24, color: "#6C6B85", fontStyle: "italic", fontSize: 13 }}>
                Loading notifications…
              </div>
            )}
            {items && items.length === 0 && (
              <div
                style={{ padding: 28, textAlign: "center" }}
                data-testid="notification-panel-empty"
              >
                <Bell size={28} strokeWidth={1.2} color="#9AA0C0" />
                <div
                  style={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontWeight: 600,
                    fontSize: 16,
                    color: "#1B1F4B",
                    marginTop: 10,
                  }}
                >
                  Nothing new yet.
                </div>
                <div style={{ color: "#6C6B85", fontSize: 13, marginTop: 4 }}>
                  We'll let you know when something changes.
                </div>
              </div>
            )}
            {items && items.length > 0 && (
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {items.map((n) => (
                  <li
                    key={n.id}
                    onClick={() => onItemClick(n)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") onItemClick(n);
                    }}
                    data-testid={`notification-item-${n.id}`}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "auto 1fr auto",
                      gap: 12,
                      padding: "12px 16px",
                      borderBottom: "1px solid #ECEBF1",
                      cursor: "pointer",
                      background: n.read ? "#FBFAFD" : "#F5F4FA",
                      transition: "background-color 140ms ease",
                    }}
                  >
                    <div style={{ paddingTop: 2 }}>
                      <CategoryIcon category={n.category} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          color: "#272636",
                          fontWeight: n.read ? 500 : 700,
                          lineHeight: 1.45,
                        }}
                      >
                        {n.body}
                      </div>
                      <div
                        className="tabular-nums"
                        style={{
                          color: "#6C6B85",
                          fontSize: 11,
                          marginTop: 4,
                          fontStyle: "italic",
                          fontFamily: '"Newsreader", Georgia, serif',
                        }}
                      >
                        {relativeTime(n.created_at)}
                      </div>
                    </div>
                    {!n.read && (
                      <span
                        aria-hidden
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 999,
                          background: "#B14E2F",
                          marginTop: 6,
                          alignSelf: "start",
                        }}
                      />
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div
            style={{
              padding: "10px 16px",
              borderTop: "1px solid #D9D8E4",
              textAlign: "right",
            }}
          >
            <button
              type="button"
              className="wn-btn wn-btn-tertiary"
              onClick={() => {
                setOpen(false);
                navigate("/notifications");
              }}
              style={{ fontSize: 12 }}
              data-testid="notification-see-all-btn"
            >
              See all notifications →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
